// Externe Bibliotheken
import { FC, useState, useContext, useRef, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router';

// Interne Module
import { checkTripEnds, checkTripStarts } from '@/_helper//checkTripStarts';
import { AppContext } from '@/context/AppContext.js';
import { ContactLayerProps } from '@/model/contactLayer/ContactLayer';
import { Icon } from '@/comp/icon/icon';

// Styles
import './contactLayer.scss';

const ContactLayer: FC<ContactLayerProps> = ({ loggedIn }) => {
    const [layerShow, setLayerShow] = useState<boolean>(false);
    const overlayRef = useRef<HTMLDivElement>(null);
    const context = useContext(AppContext);
    const location = useLocation();
    const params = useParams();

    const checkServiceTimesOnPublicHolidays = (): boolean => {
        let currentDate = new Date().valueOf(),
            startDate = new Date('2024-12-22 00:01').valueOf(),
            endDate = new Date('2025-01-01 09:10').valueOf();

        return currentDate > startDate && currentDate < endDate;
    };

    const isIndexPage = Object.keys(params).includes('index') || location.pathname === '/';

    const show = (): void => {
        setLayerShow(true);
    };

    const hide = (): void => {
        setLayerShow(false);
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent): void => {
            if (overlayRef.current && !overlayRef.current.contains(event.target as Node)) {
                setLayerShow(false);
            }
        };

        document.addEventListener('mouseup', handleOutsideClick);
        return () => {
            document.removeEventListener('mouseup', handleOutsideClick);
        };
    }, []);

    return (
        <div className="contact-layer-sec menu-item">
            <div onClick={show} className="contact-layer">
                <Icon name="help" size={30} ariaHidden />
                <span className="title">Kontakt</span>
            </div>

            {layerShow && (
                <div className="overlay" ref={overlayRef}>
                    <Icon name="close" className="close" ariaLabel="overlay schließen" role="button" onClick={hide} />

                    <div className="section">
                        <div className="headline">
                            Noch Fragen?
                            <br />
                            Wir helfen Ihnen weiter
                        </div>
                        <div className="subline">
                            Unser{' '}
                            <a
                                href={`${window.$website}/${
                                    context?.travelType === 'cruise' ? 'service/kreuzfahrt' : 'service'
                                }`}
                                rel="noopener noreferrer"
                                target="_blank">
                                FAQ
                            </a>{' '}
                            konnte Ihre Frage nicht beantworten?
                            <br />
                            Wenden Sie sich an unser Serviceteam.
                        </div>
                    </div>

                    {loggedIn && checkTripStarts() && !checkTripEnds() && (
                        <div className="section box">
                            <div className="row">
                                <Icon name="mobile" size={42} ariaHidden />
                                <div className="text-bold">
                                    Rufen Sie uns an
                                    <br />
                                    +49 (0) 89 99 82 99 531
                                </div>
                            </div>

                            {checkServiceTimesOnPublicHolidays() ? (
                                <>
                                    <div className="row">
                                        <div>
                                            Unser Service ist rund um die Uhr für Sie erreichbar. Nur während der
                                            Feiertage ändern sich die Öffnungszeiten wie folgt:
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div>
                                            24.12.2024 bis 16:30 Uhr
                                            <br />
                                            25.12.2024 ab 08:00 Uhr
                                            <br />
                                            31.12.2024 bis 20:00 Uhr
                                            <br />
                                            01.01.2025 ab 09:00 Uhr
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="row">
                                        <div> Wir sind rund um die Uhr für Sie erreichbar.</div>
                                    </div>
                                    {Boolean(context?.bookingData?.data?.allowedInteractions?.onSpot) && (
                                        <div className="row">
                                            <Link
                                                className="button"
                                                target={isIndexPage ? '_self' : '_blank'}
                                                to={`/reise-hilfe-vor-ort`}
                                                onClick={() => {
                                                    if (isIndexPage) {
                                                        hide();
                                                    }
                                                }}>
                                                Hilfe vor Ort
                                            </Link>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="section box">
                        <div className="row">
                            <Icon name="mail" size={42} ariaHidden />
                            <div className="text-bold">Kontaktieren Sie unser Serviceteam</div>
                        </div>
                        <div className="row">
                            <div>Hier können Sie Ihre individuellen Fragen platzieren.</div>
                        </div>
                        <div className="row">
                            <a
                                className="button"
                                rel="noopener noreferrer"
                                target="_blank"
                                href={`${window.$website}/service/kontakt`}>
                                Service anfragen
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactLayer;
